// The shareable token in /g/:token. Turns a region into a URL that is
// self-contained: no lookup, no database, no prior visit. Paste it to a friend and
// their browser reconstructs the exact image from the token alone, because
// generate() is deterministic from bounds.
//
// Losslessness is the hard requirement, not compactness. coordId is the uniqueness
// key for the whole product (see coordId.js and PHASE-2-PLAN.md D6), so a token
// that lost even one ULP would produce a *different specimen* than the one shared
// — a link to a near-miss of the image someone meant to show off. So we encode the
// raw IEEE-754 bits of the four bounds, which round-trips the doubles exactly:
//   4 x float64 = 32 bytes -> base64url, unpadded = a fixed 43 chars.
//
// Fixed-length and opaque, where the raw coordinate id is 45-70 chars of
// colon-separated decimals. The colon form is still accepted on the way in (see
// decodeShareToken) so a hand-written note of coordinates can be turned into a URL
// by anyone, but it is never what we generate.
//
// encode/decode use the BARE `btoa`/`atob` globals, not `window.`-prefixed ones,
// and that is load-bearing rather than tidiness: both exist in browsers, in Node
// and on Workers, but `window` exists only in the first. Every consumer of a share
// token from here on is headless — the print renderer in `scripts/`, and Block J's
// og:image handler, which has to turn the token in `/g/:token` into an image inside
// a Worker. `shareUrl` below is the one function that stays browser-only, because
// an origin is genuinely something only a browser knows.

const BYTES = 32;
export const SHARE_TOKEN_LENGTH = 43;

const toBase64Url = (base64) =>
    base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const fromBase64Url = (token) => {
    const base64 = token.replace(/-/g, '+').replace(/_/g, '/');
    // Restore the padding btoa's output had; atob requires a multiple of 4.
    const pad = base64.length % 4;
    return pad ? base64 + '='.repeat(4 - pad) : base64;
};

const boundsAreUsable = ({ realMin, realMax, imaginaryMin, imaginaryMax }) => {
    const values = [realMin, realMax, imaginaryMin, imaginaryMax];
    if (!values.every(v => typeof v === 'number' && Number.isFinite(v))) return false;
    // A degenerate or inverted region has no image to show.
    return realMax > realMin && imaginaryMax > imaginaryMin;
};

export const encodeShareToken = ({ realMin, realMax, imaginaryMin, imaginaryMax }) => {
    const bounds = { realMin, realMax, imaginaryMin, imaginaryMax };
    if (!boundsAreUsable(bounds)) return null;

    const view = new DataView(new ArrayBuffer(BYTES));
    view.setFloat64(0, realMin);
    view.setFloat64(8, realMax);
    view.setFloat64(16, imaginaryMin);
    view.setFloat64(24, imaginaryMax);

    const bytes = new Uint8Array(view.buffer);
    let binary = '';
    for (let i = 0; i < BYTES; i++) binary += String.fromCharCode(bytes[i]);

    return toBase64Url(btoa(binary));
};

// The colon-separated coordId form: exactly what coordId.js produces, so
// /g/-0.7463:-0.7462:0.1354:0.1355 works too. Parsed with Number() (not parseFloat)
// so trailing junk is rejected rather than silently truncated.
const decodeCoordForm = (value) => {
    const parts = value.split(':');
    if (parts.length !== 4) return null;
    const [realMin, realMax, imaginaryMin, imaginaryMax] = parts.map(p =>
        p.trim() === '' ? NaN : Number(p)
    );
    const bounds = { realMin, realMax, imaginaryMin, imaginaryMax };
    return boundsAreUsable(bounds) ? bounds : null;
};

// Returns the four bounds, or null for anything we cannot turn into an image — a
// truncated link, a mangled paste, an inverted region. Callers show "not found"
// rather than rendering something arbitrary.
export const decodeShareToken = (token) => {
    if (typeof token !== 'string') return null;

    // A URL may arrive percent-encoded (some clients escape the colon form).
    let value = token.trim();
    try {
        value = decodeURIComponent(value);
    } catch {
        /* malformed escape sequence: fall through with the raw value */
    }
    if (!value) return null;

    if (value.includes(':')) return decodeCoordForm(value);

    if (value.length !== SHARE_TOKEN_LENGTH || !/^[A-Za-z0-9_-]+$/.test(value)) return null;

    let binary;
    try {
        binary = atob(fromBase64Url(value));
    } catch {
        return null;
    }
    if (binary.length !== BYTES) return null;

    const view = new DataView(new ArrayBuffer(BYTES));
    for (let i = 0; i < BYTES; i++) view.setUint8(i, binary.charCodeAt(i) & 0xff);

    const bounds = {
        realMin: view.getFloat64(0),
        realMax: view.getFloat64(8),
        imaginaryMin: view.getFloat64(16),
        imaginaryMax: view.getFloat64(24)
    };
    return boundsAreUsable(bounds) ? bounds : null;
};

// The full URL to hand to someone else.
export const shareUrl = (bounds) => {
    const token = encodeShareToken(bounds);
    return token ? `${window.location.origin}/g/${token}` : null;
};
